// AI assisted development
import { Injectable, NgZone } from '@angular/core';
import { environment } from '../../../environments/environment';
import { LoggerService } from './logger-service';
import { AuthenticationService } from './authentication-service';
import { IframeEmbedService } from './iframe-embed.service';
import { isAllowedUpshopEmbedParentOrigin } from './upshop-embed-parent-origin.util';

const MESSAGE_TYPE = 'UPSHOP_CC_SESSION';

/**
 * Receives `{ type: 'UPSHOP_CC_SESSION', user }` from the Upshop parent — the JSON from SKit.core `LoginWithEmail`
 * (fetched server-side by the mono-repo backend). Applies the same session bootstrap as email/password login.
 *
 * Origin trust: HTTPS `*.upshop.com`, `upshop360.com`, `*.upshop360.com`, or localhost dev origins when not production
 * (see `upshop-embed-parent-origin.util`).
 *
 * Listeners attach only while `IframeEmbedService.isIframeEmbed` is true.
 */
@Injectable({
  providedIn: 'root',
})
export class UpshopParentSessionBridgeService {
  private handled = false;
  private messageListenerAttached = false;

  constructor(
    private readonly ngZone: NgZone,
    private readonly authenticationService: AuthenticationService,
    private readonly loggerService: LoggerService,
    private readonly iframeEmbed: IframeEmbedService,
  ) {
    if (typeof window === 'undefined') {
      return;
    }
    this.iframeEmbed.iframeEmbed$.subscribe(() => this.syncMessageListener());
    this.syncMessageListener();
  }

  private syncMessageListener(): void {
    if (typeof window === 'undefined') {
      return;
    }
    const want = this.iframeEmbed.isIframeEmbed;
    if (want === this.messageListenerAttached) {
      return;
    }
    if (want) {
      window.addEventListener('message', this.onMessage);
      this.messageListenerAttached = true;
    } else {
      window.removeEventListener('message', this.onMessage);
      this.messageListenerAttached = false;
    }
  }

  private readonly onMessage = (event: MessageEvent): void => {
    if (!this.isAllowedOrigin(event.origin)) {
      return;
    }
    const data = event.data;
    if (!data || typeof data !== 'object') {
      return;
    }
    if ((data as { type?: string }).type !== MESSAGE_TYPE) {
      return;
    }
    const user = (data as { user?: unknown }).user;
    if (!user || typeof user !== 'object') {
      return;
    }
    if (this.handled) {
      return;
    }
    this.handled = true;
    this.loggerService.logMessage('UpshopParentSessionBridge: UPSHOP_CC_SESSION received, applying session');
    this.ngZone.run(() => {
      this.authenticationService.bootstrapFromParentLoginResponse(user).catch(() => {
        this.handled = false;
      });
    });
  };

  private isAllowedOrigin(origin: string): boolean {
    return isAllowedUpshopEmbedParentOrigin(origin, environment);
  }
}
